import {Component, Input, OnChanges, OnDestroy, SimpleChanges} from '@angular/core';
import {Subscription, of} from 'rxjs';
import {catchError, map, switchMap} from 'rxjs/operators';
import {CwCaseTypeService} from '../../../service/api/cw-case-type.service';
import {CwCaseTypeTemplateService} from '../../../service/api/cw-case-type-template.service';
import {CwCaseTypeDto} from '../../../model/dto/entity/cw-case-type.dto';
import {CwCaseTypeTemplateDto} from '../../../model/dto/entity/cw-case-type-template.dto';
import {
    CwRoutingInheritanceRow,
    buildCwRoutingInheritanceRows,
} from '../../../util/cw-case-type-routing-inheritance.util';

/**
 * AI:
 * Status: "in progress"
 * Type: Component
 * SubType: CrudIntegration
 * Reason: [CW-PERSON-REFACTOR][WP-D3] Read-only panel on the CwCaseType Basisdaten tab that makes the
 *   THREE-STAGE person-routing fallback visible: it names, per axis, whether the effective address /
 *   contact routing is set on the case type itself, inherited from the referenced
 *   cw.CwCaseTypeTemplate, or missing everywhere. Renders DTO data only — no WidgetFactory, no form.
 *
 * <h3>Why a panel and not an overwrite on the generic Basisdaten form</h3>
 * The eight routing attributes are edited by the generic form in the field group
 * „Personen-Konfiguration". Painting an „geerbt" hint INTO that form would mean an
 * {@code MvsFormControlOverwrite} per field — and an {@code access} overwrite is NOT restored when a
 * later build omits the entry, so a field stamped once stays stamped. It would also be wrong on the
 * merits: an inherited value is not IN the form, the form field is genuinely EMPTY, and writing a
 * value into it would turn an inheritance into a local copy on the next save.
 *
 * <h3>What it does NOT do</h3>
 * It never resolves anything itself. The chain lives in {@code cw-case-type-routing.util.ts} (which
 * mirrors the backend's {@code CwPersonRoutingResolver}) and the rows come from
 * {@code cw-case-type-routing-inheritance.util.ts}. This class reads two rows and renders them.
 */
@Component({
    selector: 'cw-case-type-routing-inheritance',
    templateUrl: './cw-case-type-routing-inheritance.component.html',
    styleUrls: ['./cw-case-type-routing-inheritance.component.scss'],
    standalone: false
})
export class CwCaseTypeRoutingInheritanceComponent implements OnChanges, OnDestroy {

    /** The {@code cw.CwCaseType} whose routing is described. */
    @Input() caseTypeId: number;

    /** The five routing axes; empty until both reads have answered. */
    rows: CwRoutingInheritanceRow[] = [];

    /** Name of the referenced {@code cw.CwCaseTypeTemplate}, or empty when none is referenced. */
    templateName: string = '';

    /** True while a read is in flight — the panel renders nothing rather than a half answer. */
    loading: boolean = false;

    /**
     * German error text when a read failed.
     *
     * <p>Set rather than swallowed: an empty row set is indistinguishable from „nichts konfiguriert",
     * and that reading would tell a configurator his routing is missing when the truth is that it
     * could not be asked.
     */
    errorMessage: string = null;

    private subscription: Subscription = new Subscription();

    constructor(protected caseTypeService: CwCaseTypeService,
                protected caseTypeTemplateService: CwCaseTypeTemplateService) {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['caseTypeId']) {
            this.load();
        }
    }

    ngOnDestroy(): void {
        this.subscription?.unsubscribe();
    }

    /**
     * Row identity for {@code p-table}'s {@code rowTrackBy} — the axis key, which is unique per row
     * and stable across a reload, so re-reading the routing re-uses the rendered rows.
     */
    trackByRowKey(_index: number, row: CwRoutingInheritanceRow): string {
        return row.key;
    }

    /** True when at least one axis inherits — the condition for the panel's summary line. */
    get hasInheritedRows(): boolean {
        return this.rows.some(row => row.inherited);
    }

    /** True when at least one stage is configured by halves and is therefore skipped. */
    get hasWarnings(): boolean {
        return this.rows.some(row => row.warnings.length > 0);
    }

    /**
     * Reads the case type and, only if it references one, its template.
     *
     * <p>Sequential and not parallel BY NECESSITY: which template to read is an attribute of the case
     * type. A case type without a reference costs one request, not two.
     */
    private load(): void {
        this.rows = [];
        this.templateName = '';
        this.errorMessage = null;

        if (this.caseTypeId == null) {
            return;
        }

        this.loading = true;
        this.subscription.add(
            this.caseTypeService.get(this.caseTypeId).pipe(
                switchMap(result => {
                    const caseType = result as CwCaseTypeDto;
                    const templateId = caseType?.caseTypeTemplateDtoId;
                    if (templateId == null) {
                        return of({caseType, template: null as CwCaseTypeTemplateDto});
                    }
                    return this.caseTypeTemplateService.get(templateId).pipe(
                        // A template that could not be read is NOT an empty template: reporting it as
                        // one would render „nicht konfiguriert" for every axis it actually answers, so
                        // the failure travels to the catchError below rather than being defaulted here.
                        map(templateResult => ({
                            caseType,
                            template: templateResult as CwCaseTypeTemplateDto,
                        })),
                    );
                }),
                catchError(() => {
                    this.errorMessage = 'Die Routing-Konfiguration konnte nicht geladen werden.';
                    return of(null as { caseType: CwCaseTypeDto; template: CwCaseTypeTemplateDto });
                }),
            ).subscribe(result => {
                this.loading = false;
                if (!result) {
                    return;
                }
                this.templateName = result.template?.name ?? '';
                this.rows = buildCwRoutingInheritanceRows(result.caseType, result.template);
            }),
        );
    }

}
